import { useEffect, useRef } from 'react'
import Swal from 'sweetalert2'
import { useAuthStore } from './auth.store'
import { TIMEOUTS } from '@config/constants'

/**
 * Tiempo sin actividad antes de mostrar el modal de advertencia.
 * Solo aplica en frontend; no involucra al backend.
 */
const INACTIVITY_TIMEOUT = TIMEOUTS.INACTIVITY_TIMEOUT

/**
 * Duración del countdown en el modal de advertencia.
 * Si el usuario no responde en este tiempo, la sesión expira.
 */
const WARNING_DURATION = TIMEOUTS.INACTIVITY_WARNING

/** Eventos del DOM que se consideran "actividad del usuario". */
const ACTIVITY_EVENTS = [
  'mousemove', 'keydown', 'click', 'scroll', 'touchstart',
] as const

/**
 * Hook: detector de inactividad del usuario (solo frontend).
 *
 * Flujo principal:
 *  1. Al autenticarse, arma un setTimeout de INACTIVITY_TIMEOUT (4 min).
 *  2. Cualquier evento de actividad reinicia ese setTimeout.
 *  3. Si el timeout vence sin actividad, abre un Swal con countdown de WARNING_DURATION (60 s).
 *  4a. Si el usuario hace clic en "Continuar" → cierra el modal y reinicia el timer.
 *  4b. Si el countdown llega a 0 sin respuesta → expira la sesión (→ /expiredlink).
 *
 * Convive con useSessionTimer: ambos pueden disparar TIME_EXPIRED, pero
 * useSessionTimer es el límite duro; este hook avisa antes de que venza.
 *
 * Implementación con refs (sin window globals):
 *  - `inactivityTimerRef`: handle del setTimeout de inactividad.
 *  - `warningIntervalRef`: handle del setInterval del countdown en el modal.
 *  - `isWarningActiveRef`: mutex para evitar que eventos de actividad reinicien
 *    el timer mientras el modal de advertencia está abierto.
 */
export const useInactivityTracker = () => {
  const { isAuthenticated, setAuthenticated, setReasonCancellation } = useAuthStore()

  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const warningIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const isWarningActiveRef = useRef(false) // true = modal abierto, ignorar eventos

  // ---------------------------------------------------------------------------
  // Helpers — leen refs y setters estables de Zustand; no necesitan ser deps del effect.
  // ---------------------------------------------------------------------------

  /** Cancela todos los timers pendientes y marca el modal como inactivo. */
  const clearAllTimers = () => {
    if (inactivityTimerRef.current !== null) {
      clearTimeout(inactivityTimerRef.current)
      inactivityTimerRef.current = null
    }
    if (warningIntervalRef.current !== null) {
      clearInterval(warningIntervalRef.current)
      warningIntervalRef.current = null
    }
    isWarningActiveRef.current = false
  }

  /**
   * Expira la sesión por inactividad.
   * Limpia timers, cierra el modal si está abierto y actualiza el store.
   * RequireAuth reacciona y redirige a /expiredlink.
   */
  const expireSession = () => {
    clearAllTimers()
    Swal.close()
    setReasonCancellation('TIME_EXPIRED')
    setAuthenticated(false)
  }

  /**
   * (Re)arma el timer de inactividad.
   * Cancela el anterior si existía para evitar disparos duplicados.
   */
  const armInactivityTimer = () => {
    if (inactivityTimerRef.current !== null) {
      clearTimeout(inactivityTimerRef.current)
    }
    inactivityTimerRef.current = setTimeout(showWarning, INACTIVITY_TIMEOUT)
  }

  /**
   * Abre el modal de advertencia con un countdown regresivo.
   * Usa setInterval propio (no el timer de Swal) para poder actualizar el HTML.
   * El flag `isWarningActiveRef` actúa como mutex: previene abrir el modal dos veces.
   */
  const showWarning = () => {
    if (isWarningActiveRef.current) return
    isWarningActiveRef.current = true

    let secondsLeft = WARNING_DURATION / 1000

    const buildHtml = (s: number) =>
      `Sesión expirará en <strong>${s}</strong> segundo${s !== 1 ? 's' : ''} por inactividad.`

    Swal.fire({
      icon: 'warning',
      title: 'Atención',
      html: buildHtml(secondsLeft),
      showConfirmButton: true,
      confirmButtonText: 'Continuar',
      showCancelButton: false,
      allowOutsideClick: false, // evita cierre accidental
      allowEscapeKey: false,    // evita cierre accidental
      didOpen: () => {
        // Arranca el countdown: actualiza el texto cada segundo
        warningIntervalRef.current = setInterval(() => {
          secondsLeft -= 1
          const container = Swal.getHtmlContainer()
          if (container) container.innerHTML = buildHtml(secondsLeft)

          if (secondsLeft <= 0) {
            // Tiempo agotado sin respuesta del usuario
            clearInterval(warningIntervalRef.current!)
            warningIntervalRef.current = null
            expireSession()
          }
        }, 1000)
      },
      willClose: () => {
        // willClose dispara antes que .then(); garantiza que el interval
        // se cancela sin importar cómo se cerró el modal (Continuar o expireSession).
        if (warningIntervalRef.current !== null) {
          clearInterval(warningIntervalRef.current)
          warningIntervalRef.current = null
        }
      },
    }).then(result => {
      if (result.isConfirmed) {
        // Usuario hizo clic en "Continuar": libera el mutex y reinicia el ciclo
        isWarningActiveRef.current = false
        armInactivityTimer()
      }
      // result.isDismissed cubre el cierre programático de expireSession → no hacer nada
    })
  }

  /**
   * Handler de eventos de actividad del DOM.
   * Ignora eventos mientras el modal esté abierto (mutex) para no interferir
   * con el countdown ni adelantar el timer antes de que el usuario responda.
   */
  const handleActivity = () => {
    if (isWarningActiveRef.current) return
    armInactivityTimer()
  }

  // ---------------------------------------------------------------------------
  // Effect principal
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!isAuthenticated) {
      // Si el usuario cierra sesión (o aún no está autenticado), limpiar todo
      clearAllTimers()
      return
    }

    // Arrancar el ciclo de inactividad al autenticarse
    armInactivityTimer()
    ACTIVITY_EVENTS.forEach(evt =>
      document.addEventListener(evt, handleActivity, { passive: true })
    )

    return () => {
      // Cleanup: cancela timers, remueve listeners y cierra el modal si está abierto.
      // Se ejecuta cuando isAuthenticated cambia o el componente desmonta.
      clearAllTimers()
      ACTIVITY_EVENTS.forEach(evt => document.removeEventListener(evt, handleActivity))
      Swal.close()
    }
  }, [isAuthenticated]) // eslint-disable-line react-hooks/exhaustive-deps
  // Las helpers solo leen refs y setters estables de Zustand → no son deps necesarias.
}
